// Business signup, onboarding and the Delta Platform (Super Admin) portal: request and response shapes, and the
// signup field rules shared by the form and the server (the server re-checks everything).

// ---------------------------------------------------------------- signup
export interface SignupInput {
  /** Generated once by the form; a retry or a second click sends the same value, so it cannot create a second business. */
  requestId: string;
  businessName: string;
  ownerName: string;
  mobile: string;
  email: string;
  password: string;
  language: "en" | "ar";
}

export type SignupField = "businessName" | "ownerName" | "mobile" | "email" | "password";
export type SignupProblem = "required" | "too_short" | "too_long" | "mobile_format" | "email_format" | "password_rule" | "password_is_mobile";

export const SIGNUP_PASSWORD_MIN = 10;
export const SIGNUP_PASSWORD_MAX = 128;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Field-by-field problems with a signup form, after trimming. Empty when the form is valid. */
export function signupProblems(i: Partial<SignupInput>): Partial<Record<SignupField, SignupProblem>> {
  const p: Partial<Record<SignupField, SignupProblem>> = {};
  const business = (i.businessName ?? "").trim(), owner = (i.ownerName ?? "").trim();
  const mobile = (i.mobile ?? "").trim(), email = (i.email ?? "").trim(), pw = i.password ?? "";
  if (!business) p.businessName = "required"; else if (business.length < 2) p.businessName = "too_short"; else if (business.length > 200) p.businessName = "too_long";
  if (!owner) p.ownerName = "required"; else if (owner.length < 2) p.ownerName = "too_short"; else if (owner.length > 120) p.ownerName = "too_long";
  if (!mobile) p.mobile = "required"; else if (!/^05\d{8}$/.test(mobile)) p.mobile = "mobile_format";
  if (!email) p.email = "required"; else if (email.length > 200) p.email = "too_long"; else if (!EMAIL.test(email)) p.email = "email_format";
  if (!pw) p.password = "required";
  else if (pw.length > SIGNUP_PASSWORD_MAX) p.password = "too_long";
  else if (pw.length < SIGNUP_PASSWORD_MIN || !/[A-Za-z؀-ۿ]/.test(pw) || !/\d/.test(pw)) p.password = "password_rule";
  else if (pw.includes(mobile) && mobile) p.password = "password_is_mobile";
  return p;
}

export type VerificationChannel = "sms" | "email";
/** blocked: no provider is configured for the channel, so no code can be sent and the contact cannot be verified. */
export type ChannelState = "verified" | "pending" | "blocked";

/**
 * How public signup works. "closed" (the default): nobody can sign up. "manual_review": signups are accepted without
 * contact verification and Delta activates them only after a recorded identity check; this is labelled on every
 * screen and is not verification. "verified": both contacts must be verified with a code; possible only when SMS and
 * email providers are configured, and treated as closed when they are not.
 */
export type SignupMode = "closed" | "manual_review" | "verified";
/** "platform" (migration 053): the business was created by a Super Admin (Add business), not by public signup. */
export type ReviewMode = Exclude<SignupMode, "closed"> | "platform";

export interface SignupConfigDto {
  /** The mode in force: "verified" without both providers is reported as "closed". */
  mode: SignupMode;
  verification: Record<VerificationChannel, "available" | "blocked">;
  /** A trial offer a Super Admin published (migration 043); absent when none is published or signup is closed. */
  offer?: import("./packages.js").SignupOfferDto | null;
}

export interface SignupStatusDto {
  id: string;
  status: "pending_verification" | "pending_review" | "activated" | "rejected";
  businessName: string;
  ownerName: string;
  /** Masked: the browser that signed up already knows them. */
  mobile: string;
  email: string;
  createdAt: string;
  reviewMode: ReviewMode;
  verification: Record<VerificationChannel, { state: ChannelState; codeSent: boolean }>;
  /** True when the server answered a retry of the same request instead of creating anything. */
  replayed?: boolean;
}

// ---------------------------------------------------------------- onboarding
export type OnboardingStep = "organization" | "branch" | "warehouse" | "treasury" | "accounting" | "logo";
export const ONBOARDING_STEPS: OnboardingStep[] = ["organization", "branch", "warehouse", "treasury", "accounting", "logo"];
/** done; later: the owner chose to do it afterwards (accounting and logo only); todo. */
export type StepState = "done" | "later" | "todo";

export interface EntitlementsDto {
  /** subscription: a current plan; no_subscription: the platform's limits for businesses without one. */
  source: "subscription" | "no_subscription";
  plan: { code: string; nameEn: string; nameAr: string; endsOn: string; startsOn?: string; kind?: import("./packages.js").PackageKind; version?: number } | null;
  limits: { users: number; branches: number; warehouses: number; treasuries: number; posDevices: number };
  /** The package's features in force (migration 043). */
  features?: import("./packages.js").FeatureKey[];
}

export interface OnboardingDto {
  /** False for organizations Delta created itself (no wizard) and once the wizard is finished. */
  required: boolean;
  completedAt: string | null;
  steps: Record<OnboardingStep, StepState>;
  counts: { branches: number; warehouses: number; treasuries: number; accounts: number; taxTypes: number; itemKinds: number };
  entitlements: EntitlementsDto | null;
  /** Payment collection stays blocked until pricing and a payment provider are decided. */
  billing: "pending_decision";
}

// ---------------------------------------------------------------- platform
export interface PlatformMeDto { id: string; name: string; email: string }

export type OrgStatus = "pending" | "active" | "expired" | "suspended" | "rejected";

export interface UsageDto { users: number; branches: number; warehouses: number; treasuries: number; posDevices: number; lastSignInAt: string | null }

export interface PlatformOrgRow {
  id: string; nameEn: string; nameAr: string; status: OrgStatus; origin: "provisioned" | "signup"; createdAt: string;
  signup: { status: SignupStatusDto["status"]; mobileVerified: boolean; emailVerified: boolean } | null;
  onboarding: { done: number; total: number; completedAt: string | null } | null;
  subscription: { planCode: string; endsOn: string; kind?: import("./packages.js").PackageKind } | null;
  /** Migration 043: trial, paid, expired, none or provisioned (one state per business). */
  packageState?: import("./packages.js").PackageState;
  /** Migration 044: legacy (earlier rules, kept for businesses that existed) or enforced (no package in force = no access). */
  packageRules?: "legacy" | "enforced";
  /** Migration 044: open, or expired / none when the business is under package rules and nothing is in force today. */
  access?: import("./packages.js").PackageAccess;
  usage: UsageDto;
}

export interface PlatformSignupDto {
  id: string; status: SignupStatusDto["status"]; businessName: string; ownerName: string; mobile: string; email: string | null; language: "en" | "ar";
  createdAt: string; reviewMode: ReviewMode; mobileVerifiedAt: string | null; emailVerifiedAt: string | null;
  verification: Record<VerificationChannel, ChannelState>;
  activatedAt: string | null; activatedBy: string | null; activationMethod: "verified" | "manual_attestation" | "platform_created" | null; attestationNote: string | null;
  rejectedAt: string | null; rejectReason: string | null;
  /** Migration 053: the Super Admin who created the business with Add business (review mode "platform"). */
  createdBy?: string | null;
}

export interface SubscriptionDto {
  id: string; plan: { id: string; code: string; nameEn: string; nameAr: string }; startsOn: string; endsOn: string;
  status: "active" | "cancelled"; current: boolean; source: "manual_grant" | "signup_offer"; reason: string; grantedBy: string; grantedAt: string;
  cancelledAt: string | null; cancelReason: string | null;
  /** Migration 043: the package version and kind, how it came about, and any payment recorded by hand. */
  kind: import("./packages.js").PackageKind; version: number; changeKind: import("./packages.js").ChangeKind;
  /** scheduled: starts after today; ended: its end date has passed. */
  timing: "current" | "scheduled" | "ended" | "cancelled";
  originalEndsOn: string | null;
  payment: { kind: import("./packages.js").SubscriptionPayment; reference: string | null; amount: string | null; currency: string | null; recordedAt: string | null; recordedBy: string | null };
  terms: import("./packages.js").PackageTerms;
}

export interface RecoveryDto { id: string; userName: string; channel: VerificationChannel; status: "blocked" | "code_sent" | "completed" | "cancelled"; reason: string; requestedBy: string; requestedAt: string; completedAt: string | null }

export interface PlatformOrgDetail extends PlatformOrgRow {
  statusReason: string | null; statusChangedAt: string | null;
  administrators: { id: string; name: string; mobile: string; active: boolean }[];
  signupDetail: PlatformSignupDto | null;
  entitlements: EntitlementsDto | null;
  subscriptions: SubscriptionDto[];
  recoveries: RecoveryDto[];
  onboardingSteps: Record<OnboardingStep, StepState> | null;
  history: PlatformAuditDto[];
  /** Migration 043: renewal and change requests from the owner, and whether the business has had its one trial. */
  requests: import("./packages.js").SubscriptionRequestDto[];
  trialUsed: boolean;
  today: string;
  /** The signup offer recorded when the business signed up, if any (a trial the activation may start). */
  offer: { planId: string; nameEn: string; nameAr: string; trialDays: number } | null;
  /** Migration 048: the reseller managing the business, if any. */
  reseller?: { id: string; code: string; nameEn: string; nameAr: string } | null;
  /** Migration 053: the owner's setup codes (never the code itself), newest first. */
  ownerSetups?: OwnerSetupDto[];
}

// ---------------------------------------------------------------- Add business (migration 053)
/** What the new business gets: a paid package, a trial of a trial package, or nothing yet (pending, no access). */
export type NewBusinessAccess = "paid" | "trial" | "pending";
/** How the owner's setup code reaches them: a configured provider, or by hand (labelled "not sent"). */
export type SetupDelivery = "sms" | "email" | "manual";

export interface NewBusinessInput {
  /** Generated once per form; a retry or a second click sends the same value and creates nothing more. */
  requestId: string;
  businessName: string;
  language: "en" | "ar";
  ownerName: string;
  mobile: string;
  /** Optional. */
  email?: string;
  access: NewBusinessAccess;
  planId?: string;
  /** Paid only: the subscription's dates (a trial runs its package's days from today). */
  startsOn?: string;
  endsOn?: string;
  /** How the owner's identity was checked and why the business is added (audited; at least 10 characters). */
  reason: string;
  delivery: SetupDelivery;
  /** Required when another business already has exactly this name. */
  confirmSameName?: boolean;
}

export type NewBusinessField = "businessName" | "language" | "ownerName" | "mobile" | "email" | "access" | "planId" | "startsOn" | "endsOn" | "reason" | "delivery";
export type NewBusinessProblem = SignupProblem | "date_format" | "before_start" | "in_past";

export const NEW_BUSINESS_REASON_MIN = 10;

/** Field-by-field problems with an Add business form, after trimming. Empty when the form is valid. */
export function newBusinessProblems(i: Partial<NewBusinessInput>): Partial<Record<NewBusinessField, NewBusinessProblem>> {
  const p: Partial<Record<NewBusinessField, NewBusinessProblem>> = {};
  const business = (i.businessName ?? "").trim(), owner = (i.ownerName ?? "").trim();
  const mobile = (i.mobile ?? "").trim(), email = (i.email ?? "").trim(), reason = (i.reason ?? "").trim();
  if (!business) p.businessName = "required"; else if (business.length < 2) p.businessName = "too_short"; else if (business.length > 200) p.businessName = "too_long";
  if (i.language !== "en" && i.language !== "ar") p.language = "required";
  if (!owner) p.ownerName = "required"; else if (owner.length < 2) p.ownerName = "too_short"; else if (owner.length > 120) p.ownerName = "too_long";
  if (!mobile) p.mobile = "required"; else if (!/^05\d{8}$/.test(mobile)) p.mobile = "mobile_format";
  if (email && email.length > 200) p.email = "too_long"; else if (email && !EMAIL.test(email)) p.email = "email_format";
  if (!["paid", "trial", "pending"].includes(i.access ?? "")) p.access = "required";
  if ((i.access === "paid" || i.access === "trial") && !i.planId) p.planId = "required";
  if (i.access === "paid") {
    const d = /^\d{4}-\d{2}-\d{2}$/;
    if (!d.test(i.startsOn ?? "")) p.startsOn = i.startsOn ? "date_format" : "required";
    if (!d.test(i.endsOn ?? "")) p.endsOn = i.endsOn ? "date_format" : "required";
    else if (!p.startsOn && i.endsOn! < i.startsOn!) p.endsOn = "before_start";
  }
  if (!reason) p.reason = "required"; else if (reason.length < NEW_BUSINESS_REASON_MIN) p.reason = "too_short"; else if (reason.length > 1000) p.reason = "too_long";
  if (!["sms", "email", "manual"].includes(i.delivery ?? "")) p.delivery = "required";
  else if (i.delivery === "email" && !email) p.email = "required";
  return p;
}

export interface NewBusinessFormDto {
  today: string;
  /** Which delivery channels have a provider configured; manual delivery is always possible. */
  providers: Record<VerificationChannel, "configured" | "not_configured">;
  /** Active packages only (paid ones to grant, trial ones to start as the business's one trial). */
  plans: PlanDto[];
}

export interface NewBusinessPreviewDto {
  business: { name: string; language: "en" | "ar" };
  owner: { name: string; mobile: string; email: string | null };
  /** active with a package or trial; pending when nothing is granted (no access until activated). */
  status: "active" | "pending";
  access: NewBusinessAccess;
  package: {
    planId: string; code: string; nameEn: string; nameAr: string; kind: import("./packages.js").PackageKind; version: number;
    startsOn: string; endsOn: string; trialDays: number | null;
    limits: { users: number; branches: number; warehouses: number; treasuries: number; posDevices: number };
    features: import("./packages.js").FeatureKey[];
  } | null;
  delivery: SetupDelivery;
  setupHours: number;
  /** Businesses that already have exactly this name; creating another needs confirmSameName. */
  sameName: { id: string; nameEn: string; nameAr: string; status: OrgStatus }[];
}

/** A setup code just issued. code and link are present only for manual delivery, only in this one answer. */
export interface OwnerSetupIssuedDto { id: string; delivery: SetupDelivery; destination: string | null; expiresAt: string; code: string | null; link: string | null }

export interface NewBusinessResultDto {
  organizationId: string;
  /** True when this answers a retry of a request already handled: nothing was created and no code is shown again. */
  replayed: boolean;
  setup: OwnerSetupIssuedDto | null;
}

export interface OwnerSetupDto {
  id: string; delivery: SetupDelivery; destination: string | null; status: "open" | "used" | "expired" | "cancelled";
  expiresAt: string; createdAt: string; createdBy: string; consumedAt: string | null;
}

/** The original plan body (migration 027). Package fields added by migration 043 are optional here. */
export type PlanInput = Pick<import("./packages.js").PackageInput, "code" | "nameEn" | "nameAr" | "maxUsers" | "maxBranches" | "maxWarehouses" | "maxTreasuries" | "maxPosDevices" | "active">
  & Partial<import("./packages.js").PackageInput>;
export type PlanDto = import("./packages.js").PlatformPackageDto;

export interface PlatformSettingsDto {
  signupMode: SignupMode;
  /** Read-only: whether "verified" can be chosen (both SMS and email providers configured). */
  providers: Record<VerificationChannel, "configured" | "not_configured">;
  noSubscription: { users: number; branches: number; warehouses: number; treasuries: number; posDevices: number };
  /** Migration 043: features with no current subscription, the published signup offer, and the expiry warning window. */
  noSubscriptionFeatures: import("./packages.js").FeatureKey[];
  signupOfferPlanId: string | null;
  expiryWarningDays: number;
  updatedAt: string;
}

export interface PlatformAuditDto {
  id: string; at: string; actor: string | null; action: string; organizationId: string | null; organizationName: string | null;
  entity: string | null; entityId: string | null; reason: string | null; meta: unknown;
  /** What the action changed, as recorded (shown under "Details" in the readable activity). */
  before?: unknown; after?: unknown;
}

export interface PlatformHealthDto {
  database: { ok: boolean; latencyMs: number; migrations: number; latestMigration: string | null };
  providers: { email: "configured" | "not_configured"; sms: "configured" | "not_configured"; payment: "not_configured" };
  signups: { mode: SignupMode; pendingVerification: number; pendingReview: number };
  organizations: Record<OrgStatus, number>;
  errors: { at: string; method: string; route: string; status: number; code: string }[];
  uptimeSeconds: number;
}
